import { useState } from 'react';
import {
  Location,
  SmspoolCountry,
  PvaDealsState,
  DurianRcsCountry,
  GenericApiLocation,
} from '../../types/api';
import { getFlagUrl, getCountryCodeFromName } from '../../utils/countryMapper';

interface LocationCardProps {
  location: Location;
  locationType: 'countries' | 'states' | 'areaCodes';
  isSelected?: boolean;
  onSelect: (location: Location) => void;
}

function isSmspoolCountry(location: Location): location is SmspoolCountry {
  return 'country' in location && 'country_id' in location;
}

function isPvaDealsState(location: Location): location is PvaDealsState {
  return 'name' in location && 'shortName' in location && 'value' in location;
}

function isDurianRcsCountry(location: Location): location is DurianRcsCountry {
  return 'name' in location && 'code' in location;
}

function isGenericApiLocation(location: Location): location is GenericApiLocation {
  const loc = location as unknown as Record<string, unknown>;
  return typeof loc.name === 'string';
}

export function LocationCard({
  location,
  locationType,
  isSelected,
  onSelect,
}: LocationCardProps) {
  const getLocationName = (): string => {
    if (isSmspoolCountry(location)) {
      return location.country || '';
    }
    if (isPvaDealsState(location)) {
      return location.name || '';
    }
    if (isDurianRcsCountry(location)) {
      return location.name || '';
    }
    if (isGenericApiLocation(location)) {
      const state = location.state || '';
      const areaCode = location.areaCode || '';
      if (state && areaCode) return `${state} (${areaCode})`;
      return String(location.name ?? '');
    }
    return 'Unknown Location';
  };

  const getLocationCode = (): string => {
    if (isSmspoolCountry(location)) {
      return location.country_id;
    }
    if (isPvaDealsState(location)) {
      return location.shortName;
    }
    if (isDurianRcsCountry(location)) {
      return location.code;
    }
    if (isGenericApiLocation(location)) {
      if (locationType === 'areaCodes') {
        return String(location.areaCode ?? location.id ?? '');
      }
      return String(location.short_name ?? '');
    }
    return '';
  };

  const getFlagCode = (): string | null => {
    if (isSmspoolCountry(location)) {
      const id = (location.country_id || '').trim();
      if (id.length === 2) return id.toUpperCase();
      const codeFromName = getCountryCodeFromName(location.country || '');
      return codeFromName;
    }
    if (isDurianRcsCountry(location) && locationType === 'countries') {
      const code = (location.code || '').trim();
      return code.length === 2 ? code.toUpperCase() : null;
    }
    if (isGenericApiLocation(location) && locationType === 'countries') {
      const short = location.short_name?.trim();
      if (short && short.length === 2) return short.toUpperCase();
      return getCountryCodeFromName(location.name || '');
    }
    return null;
  };

  const getLocationPrice = (): string | null => {
    const locAny = location as unknown as Record<string, unknown>;
    const rawPrice = locAny.price;
    if (rawPrice != null && rawPrice !== '') {
      const num = parseFloat(String(rawPrice));
      return Number.isFinite(num) ? num.toFixed(2) : null;
    }
    return null;
  };

  const priceStr = getLocationPrice();
  const flagCode = getFlagCode();
  const flagUrl =
    flagCode && flagCode.length === 2 ? getFlagUrl(flagCode) : null;
  const [flagError, setFlagError] = useState(false);
  const showFlag = flagUrl && !flagError;

  return (
    <button
      onClick={() => onSelect(location)}
      className={`w-full card-glass rounded-lg p-4 hover:shadow-lg transition-all duration-300 text-left group ${
        isSelected
          ? 'border-brand-accent shadow-brand-accent/20 scale-105'
          : 'border-neutral-dark hover:border-brand-accent/50 hover:shadow-brand-accent/20 hover:scale-105'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {showFlag && (
            <div className="w-9 h-9 rounded-full bg-neutral-dark/80 flex items-center justify-center overflow-hidden shrink-0 flex-shrink-0">
              <img
                src={flagUrl}
                alt=""
                loading="lazy"
                className="w-8 h-8 object-cover"
                onError={() => setFlagError(true)}
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-heading font-semibold text-neutral-light group-hover:text-brand-accent transition-colors truncate">
              {getLocationName()}
            </h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {getLocationCode() && (
                <p className="text-sm text-neutral-medium font-body">
                  {getLocationCode()}
                </p>
              )}
              {priceStr !== null && (
                <p className="text-sm font-body font-semibold text-brand-accent">
                  {priceStr} USD
                </p>
              )}
            </div>
          </div>
        </div>
        {isSelected && (
          <div className="ml-4">
            <div className="w-6 h-6 rounded-full bg-brand-accent flex items-center justify-center">
              <svg
                className="w-4 h-4 text-brand-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
        )}
      </div>
    </button>
  );
}

